import { Modal } from "react-bootstrap";
import {
	FaEye,
	FaClock,
	FaUser,
	FaIdCard,
	FaCalendarAlt,
	FaExclamationTriangle,
} from "react-icons/fa";
import { timeToString } from "@/utils/dateUtil";
import { AttendanceTimeDetails } from "@/services/sintegrada/attendance/getRiskClassificationDashboard";

type Props = {
	show: boolean;
	onHide: () => void;
	maxTimeAttendanceDetails: AttendanceTimeDetails;
	minTimeAttendanceDetails: AttendanceTimeDetails;
	maxWaitTimeAttendanceDetails: AttendanceTimeDetails;
	minWaitTimeAttendanceDetails: AttendanceTimeDetails;
};

export function TimeDetailsModal({
	show,
	onHide,
	maxTimeAttendanceDetails,
	minTimeAttendanceDetails,
	maxWaitTimeAttendanceDetails,
	minWaitTimeAttendanceDetails,
}: Props) {
	const renderAttendanceCard = (
		title: string,
		timeValue: number,
		details: AttendanceTimeDetails,
		isWaitTime: boolean = false,
		isMax: boolean = false
	) => (
		<div className="card h-100 border-0 shadow">
			<div
				className="card-header d-flex align-items-center justify-content-between"
				style={{
					backgroundColor: isMax ? "#fff5f5" : "#f0f9ff",
					color: isMax ? "#991b1b" : "#1e40af",
					border: "none",
					borderBottom: isMax ? "3px solid #dc2626" : "3px solid #2563eb",
				}}
			>
				<h6 className="mb-0 d-flex align-items-center fw-bold">
					<FaExclamationTriangle className="me-2" />
					{title}
				</h6>
				<div
					className="badge fs-6 px-3 py-2"
					style={{
						backgroundColor: isMax ? "#dc2626" : "#2563eb",
						color: "white",
						fontWeight: "600",
					}}
				>
					{timeToString(timeValue)}
				</div>
			</div>
			<div className="card-body bg-white">
				{/* Classificação de Risco */}
				<div className="mb-4 p-3 rounded" style={{ backgroundColor: "#f8fafc" }}>
					<div className="d-flex align-items-center justify-content-between">
						<div className="d-flex align-items-center">
							<div
								className="rounded-circle me-3"
								style={{
									width: "24px",
									height: "24px",
									backgroundColor: details.risk_classification_color,
									border: "3px solid #e2e8f0",
								}}
							/>
							<span className="fw-bold text-dark">Classificação de Risco:</span>
						</div>
						<span
							className="badge fs-6 px-3 py-2"
							style={{
								backgroundColor: details.risk_classification_color,
								color: "#fff",
								fontWeight: "600",
							}}
						>
							{details.risk_classification_name}
						</span>
					</div>
				</div>

				{/* Informações do Atendimento */}
				<div className="row g-3">
					<div className="col-6">
						<div className="p-3 rounded border" style={{ backgroundColor: "#f8fafc" }}>
							<div className="d-flex align-items-center mb-2">
								<FaIdCard className="text-primary me-2" />
								<small className="text-muted fw-bold">ID Atendimento</small>
							</div>
							<div className="fw-bold text-dark fs-6">{details.attendance_id}</div>
						</div>
					</div>
					<div className="col-6">
						<div className="p-3 rounded border" style={{ backgroundColor: "#f8fafc" }}>
							<div className="d-flex align-items-center mb-2">
								<FaCalendarAlt className="text-primary me-2" />
								<small className="text-muted fw-bold">Data/Hora</small>
							</div>
							<div className="fw-bold text-dark fs-6">{details.attendance_date}</div>
						</div>
					</div>
					<div className="col-12">
						<div className="p-3 rounded border" style={{ backgroundColor: "#f8fafc" }}>
							<div className="d-flex align-items-center mb-2">
								<FaUser className="text-primary me-2" />
								<small className="text-muted fw-bold">Paciente</small>
							</div>
							<div className="fw-bold text-dark fs-6">{details.patient_name}</div>
						</div>
					</div>
					<div className="col-12">
						<div className="p-3 rounded border" style={{ backgroundColor: "#f8fafc" }}>
							<div className="d-flex align-items-center mb-2">
								<FaIdCard className="text-primary me-2" />
								<small className="text-muted fw-bold">CPF</small>
							</div>
							<div className="fw-bold text-dark fs-6">{details.patient_cpf}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<Modal size="xl" show={show} onHide={onHide} centered>
			<Modal.Header closeButton className="bg-primary text-white border-0">
				<Modal.Title className="d-flex align-items-center fw-bold">
					<FaEye className="me-2" />
					Detalhes dos Tempos - Classificação de Risco
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="p-4" style={{ backgroundColor: "#f1f5f9" }}>
				<div className="row g-4">
					{/* Seção Tempo de Atendimento */}
					<div className="col-12">
						<div className="card border-0 shadow">
							<div className="card-header bg-white border-bottom">
								<h5 className="mb-0 d-flex align-items-center text-dark fw-bold">
									<FaClock className="me-2 text-primary" />
									Tempo de Atendimento
								</h5>
							</div>
							<div className="card-body bg-white">
								<div className="row g-4">
									<div className="col-md-6">
										{renderAttendanceCard(
											"⏱️ Tempo Máximo",
											maxTimeAttendanceDetails.time_in_seconds || 0,
											maxTimeAttendanceDetails,
											false,
											true
										)}
									</div>
									<div className="col-md-6">
										{renderAttendanceCard(
											"⚡ Tempo Mínimo",
											minTimeAttendanceDetails.time_in_seconds || 0,
											minTimeAttendanceDetails,
											false,
											false
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Seção Tempo de Espera */}
					<div className="col-12">
						<div className="card border-0 shadow">
							<div className="card-header bg-white border-bottom">
								<h5 className="mb-0 d-flex align-items-center text-dark fw-bold">
									<FaClock className="me-2 text-primary" />
									Tempo de Espera
								</h5>
							</div>
							<div className="card-body bg-white">
								<div className="row g-4">
									<div className="col-md-6">
										{renderAttendanceCard(
											"⏰ Espera Máxima",
											maxWaitTimeAttendanceDetails.wait_time_in_seconds || 0,
											maxWaitTimeAttendanceDetails,
											true,
											true
										)}
									</div>
									<div className="col-md-6">
										{renderAttendanceCard(
											"🚀 Espera Mínima",
											minWaitTimeAttendanceDetails.wait_time_in_seconds || 0,
											minWaitTimeAttendanceDetails,
											true,
											false
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Modal.Body>
		</Modal>
	);
}
